/**
 * Funnel Performance Section
 * Section 2: Conversion rates, retention, cohort analysis
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';

interface FunnelPerformanceProps {
  data?: {
    conversionRates?: {
      awarenessToLead?: number;
      leadToTrial?: number;
      trialToActivation?: number;
      activationToPaid?: number;
    };
    retention?: {
      day30?: number;
      day60?: number;
      day90?: number;
    };
    cohortAnalysis?: Array<{
      icp: string;
      plan: string;
      conversionRate: number;
    }>;
  };
}

export function FunnelPerformance({ data }: FunnelPerformanceProps) {
  const formatPercent = (value?: number) => {
    if (value === undefined) return '0%';
    return `${value.toFixed(2)}%`;
  };

  const funnelSteps = [
    { label: 'Awareness → Lead', value: data?.conversionRates?.awarenessToLead },
    { label: 'Lead → Trial', value: data?.conversionRates?.leadToTrial },
    { label: 'Trial → Activation', value: data?.conversionRates?.trialToActivation },
    { label: 'Activation → Paid', value: data?.conversionRates?.activationToPaid },
  ];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Conversion Funnel</CardTitle>
          <CardDescription>Conversion rates at each stage</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {funnelSteps.map((step, index) => (
              <div key={index} className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>{step.label}</span>
                  <span className="font-semibold">{formatPercent(step.value)}</span>
                </div>
                <Progress value={step.value || 0} className="h-2" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">30-Day Retention</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatPercent(data?.retention?.day30)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">60-Day Retention</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatPercent(data?.retention?.day60)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">90-Day Retention</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatPercent(data?.retention?.day90)}</div>
          </CardContent>
        </Card>
      </div>

      {data?.cohortAnalysis && data.cohortAnalysis.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Cohort Analysis</CardTitle>
            <CardDescription>Conversion by ICP × Plan</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.cohortAnalysis.map((cohort, index) => (
                <div key={index} className="flex justify-between items-center p-2 border rounded">
                  <div>
                    <span className="font-medium">{cohort.icp}</span>
                    <span className="text-muted-foreground ml-2">• {cohort.plan}</span>
                  </div>
                  <span className="font-semibold">{formatPercent(cohort.conversionRate)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
